import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { api, ApiError } from '@/lib/api';
import { DemoToolsCard } from '@/components/DemoToolsCard';
import { SPORT_IDS, getSportLabel } from '@huddle/shared';
import {
  ArrowLeft,
  ExternalLink,
  Megaphone,
  UserPlus,
  Flag,
  Award,
  Trophy,
  RefreshCw,
  ImagePlus,
} from 'lucide-react';

const TEAM_MODES = ['none', '2_teams', 'n_teams', 'pairs', 'individual'] as const;
const SCORING_MODES = ['none', 'team_score', 'sets', 'individual_time'] as const;
const SIGNUP_MODES = ['open', 'invite', 'priority', 'geo_open'] as const;
const FORMATS = ['pickup', 'league_match', 'tournament', 'training'] as const;

interface AdminVenueListItem {
  id: string;
  name: string;
  cityId: string | null;
  city?: string | null;
}

interface RosterEntry {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  signupStatus: string | null;
  played: boolean | null;
  noShow: boolean | null;
}

interface Detail {
  id: string;
  title: string | null;
  sportType: string;
  format: string;
  status: string;
  date: string;
  time: string;
  durationMinutes: number;
  orgId: string | null;
  venueId: string | null;
  locationText: string | null;
  teamMode: string;
  scoringMode: string;
  maxParticipants: number | null;
  hasParticipantLimit: boolean;
  signupMode: string;
  priorityLocked: boolean;
  isPublic: boolean;
  isRated: boolean;
  hasAnnouncement: boolean;
  announcementTime: string | null;
  createdBy: string;
  createdAt: string;
  creator: { id: string; firstName: string | null; lastName: string | null; email: string | null } | null;
  venue: { id: string; name: string; address: string | null; cityId: string | null } | null;
  roster: RosterEntry[];
  signupsCount: number;
  participantsCount: number;
  hasResults: boolean;
}

type TgMessageKind = 'announcement' | 'signup' | 'end' | 'mvp' | 'score';

interface TelegramData {
  chatId: string | null;
  chatLink: string | null;
  messages: Array<{
    kind: TgMessageKind | string;
    messageId: string | null;
    link: string | null;
    stateNote: string;
  }>;
  photos: { ogShare: string; leaderboard: string | null };
  flags: {
    startNotified: boolean;
    endNotified: boolean;
    mvpVotingClosed: boolean;
    hasAnnouncement: boolean;
  };
}

interface MvpVoter {
  userId: string;
  firstName: string | null;
  lastName: string | null;
}

interface MvpVotesData {
  mvpVotingClosed: boolean;
  totalParticipants: number;
  totalVoters: number;
  voteTally: Array<{
    userId: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    voteCount: number;
    voters: MvpVoter[];
  }>;
  pendingVoters: Array<{
    userId: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  }>;
  /** Determined MVP(s) — the winner(s) per finalizeMvp (per-team + underdog
   *  tiebreak). Populated once voting closes & stats finalize. */
  mvps: Array<{
    userId: string;
    firstName: string | null;
    lastName: string | null;
    team: string | null;
    votes: number | null;
    tiebreaker: boolean;
  }>;
}

const TG_KIND_LABEL: Record<string, string> = {
  announcement: 'Анонс',
  signup: 'Запись',
  end: 'Завершение',
  mvp: 'MVP-голосование',
  score: 'Счёт',
};

function TgIcon({ kind }: { kind: string }) {
  switch (kind) {
    case 'announcement':
      return <Megaphone className="h-3.5 w-3.5" />;
    case 'signup':
      return <UserPlus className="h-3.5 w-3.5" />;
    case 'end':
      return <Flag className="h-3.5 w-3.5" />;
    case 'mvp':
      return <Award className="h-3.5 w-3.5" />;
    case 'score':
      return <Trophy className="h-3.5 w-3.5" />;
    default:
      return null;
  }
}

function tgToneFor(
  kind: string,
  messageId: string | null,
  stateNote: string,
  flags: TelegramData['flags'],
): 'success' | 'warning' | 'muted' | 'secondary' {
  if (kind === 'mvp' && flags.mvpVotingClosed) return 'warning';
  if (messageId !== null) return 'success';
  if (stateNote === 'Ожидает отправки') return 'warning';
  return 'muted';
}

function fullName(p: { firstName: string | null; lastName: string | null; userId: string }) {
  const name = [p.firstName, p.lastName].filter(Boolean).join(' ');
  return name || p.userId.slice(0, 8);
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium mb-3">
      {children}
    </div>
  );
}

function heroTitle(d: Detail) {
  if (d.title && d.title.trim()) return d.title;
  return `${d.sportType} · ${d.date}`;
}

function rosterName(r: RosterEntry) {
  return [r.firstName, r.lastName].filter(Boolean).join(' ') || r.email || r.userId.slice(0, 8);
}

export function ActivityDetailPage() {
  const { id } = useParams({ from: '/activities/$id' });
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'activity', id],
    queryFn: () => api.get<{ data: Detail }>(`/api/admin/activities/${id}`).then((r) => r.data),
  });

  const { data: venueList } = useQuery({
    queryKey: ['admin', 'venues', 'picker'],
    queryFn: () =>
      api
        .get<{ data: AdminVenueListItem[] }>(`/api/admin/venues?limit=200`)
        .then((r) => r.data),
    staleTime: 5 * 60_000,
  });

  const { data: telegramData } = useQuery({
    queryKey: ['admin', 'activity', id, 'telegram'],
    queryFn: () =>
      api
        .get<{ data: TelegramData }>(`/api/admin/activities/${id}/telegram`)
        .then((r) => r.data),
  });

  const { data: mvpData } = useQuery({
    queryKey: ['admin', 'activity', id, 'mvp-votes'],
    queryFn: () =>
      api
        .get<{ data: MvpVotesData }>(`/api/admin/activities/${id}/mvp-votes`)
        .then((r) => r.data),
  });

  const [expandedVoters, setExpandedVoters] = useState<Record<string, boolean>>({});

  const [form, setForm] = useState<Partial<Detail> | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [finishOpen, setFinishOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [rosterRemovingId, setRosterRemovingId] = useState<string | null>(null);

  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.patch<{ data: Detail }>(`/api/admin/activities/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'activity', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'activities'] });
    },
  });

  const cancel = useMutation({
    mutationFn: (reason: string | undefined) =>
      api.post<{ data: { id: string; status: 'cancelled' } }>(
        `/api/admin/activities/${id}/cancel`,
        { reason },
      ),
    onSuccess: () => {
      setCancelOpen(false);
      setCancelReason('');
      qc.invalidateQueries({ queryKey: ['admin', 'activity', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'activities'] });
    },
  });

  const finish = useMutation({
    mutationFn: () =>
      api.post<{ data: { id: string; status: 'finished' } }>(
        `/api/admin/activities/${id}/finish`, {},
      ),
    onSuccess: () => {
      setFinishOpen(false);
      qc.invalidateQueries({ queryKey: ['admin', 'activity', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'activities'] });
    },
  });

  const repost = useMutation({
    mutationFn: (kind: 'announcement' | 'signup' | 'finish' | 'mvp') =>
      api.post<{
        data: {
          reposted: boolean;
          deletedTextMessageId: string | null;
          deletedPhotoMessageId: string | null;
        };
      }>(`/api/admin/activities/${id}/telegram/repost`, { kind, confirm: 'REPOST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'activity', id, 'telegram'] });
    },
  });

  const resendPhoto = useMutation({
    mutationFn: (kind: 'announcement' | 'signup' | 'finish' | 'mvp') =>
      api.post<{
        data: { photoMessageId: string | null; previousPhotoMessageId: string | null };
      }>(`/api/admin/activities/${id}/telegram/resend-photo`, { kind, confirm: 'PHOTO' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'activity', id, 'telegram'] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Не удалось прислать картинку';
      window.alert(`Картинка не отправилась.\n\n${msg}`);
    },
  });

  const reopen = useMutation({
    mutationFn: () =>
      api.post<{ data: { id: string; status: 'open' } }>(
        `/api/admin/activities/${id}/reopen`, {},
      ),
    onSuccess: () => {
      setReopenOpen(false);
      qc.invalidateQueries({ queryKey: ['admin', 'activity', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'activities'] });
    },
  });

  // Manually advance a stuck game's lifecycle: post MVP voting (if eligible &
  // not posted) or publish final stats. Unsticks games whose post-finalize
  // step didn't fire.
  const advanceLifecycle = useMutation({
    mutationFn: () =>
      api.post<{ data: { id: string; action: string; reason?: string } }>(
        `/api/admin/activities/${id}/advance-lifecycle`, {},
      ),
    onSuccess: (res) => {
      const action = res?.data?.action;
      const label =
        action === 'posted_mvp_voting' ? 'Отправлено MVP-голосование'
        : action === 'published_stats' ? 'Опубликован счёт'
        : 'Цикл уже завершён';
      window.alert(label);
      qc.invalidateQueries({ queryKey: ['admin', 'activity', id, 'telegram'] });
      qc.invalidateQueries({ queryKey: ['admin', 'activity', id] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Не удалось продвинуть цикл';
      window.alert(`Не получилось.\n\n${msg}`);
    },
  });

  const rosterRemove = useMutation({
    mutationFn: (userId: string) =>
      api.post<{ data: { ok: true } }>(
        `/api/admin/activities/${id}/roster/${userId}/remove`, {},
      ),
    onMutate: (userId) => setRosterRemovingId(userId),
    onSettled: () => setRosterRemovingId(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'activity', id] });
    },
  });

  const patchPayload = useMemo(() => {
    if (!form || !data) return {};
    const keys: (keyof Detail)[] = [
      'title', 'date', 'time', 'durationMinutes', 'sportType', 'format',
      'teamMode', 'scoringMode', 'maxParticipants', 'hasParticipantLimit',
      'venueId', 'locationText', 'signupMode', 'isPublic', 'isRated',
      'hasAnnouncement', 'announcementTime',
    ];
    const out: Record<string, unknown> = {};
    for (const k of keys) {
      if (form[k] !== data[k]) out[k] = form[k];
    }
    return out;
  }, [form, data]);

  if (isLoading) return <div className="p-4 sm:p-6 lg:p-8 text-zinc-500 text-sm">Загрузка…</div>;
  if (error || !data || !form) return <div className="p-4 sm:p-6 lg:p-8 text-rose-300 text-sm">Ошибка загрузки</div>;

  const venueOptions: ComboboxOption[] = (venueList ?? []).map((v) => ({
    value: v.id,
    label: v.name,
    hint: v.city ?? v.cityId ?? undefined,
  }));

  const isCancelled = data.status === 'cancelled';
  const isFinished = data.status === 'finished';
  const creatorLabel = data.creator
    ? [data.creator.firstName, data.creator.lastName].filter(Boolean).join(' ')
        || data.creator.email
        || data.creator.id.slice(0, 8)
    : data.createdBy.slice(0, 8);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl space-y-6">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate({ to: '/activities' })}
        className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors"
      >
        <ArrowLeft className="h-3 w-3" /> К списку
      </button>

      {/* Hero */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
        <div className="space-y-2 flex-1 min-w-0">
          <h1 className="text-2xl font-semibold tracking-[-0.01em] text-zinc-50">{heroTitle(data)}</h1>
          <div className="flex items-center gap-2 flex-wrap text-sm text-zinc-400">
            <StatusBadge status={data.status} />
            <Badge variant="secondary">{data.sportType}</Badge>
            <span className="font-mono tabular-nums text-zinc-300">{data.date} · {data.time?.slice(0, 5)}</span>
            {data.venue ? (
              <span className="text-zinc-500">· {data.venue.name}</span>
            ) : data.locationText ? (
              <span className="text-zinc-500">· {data.locationText}</span>
            ) : null}
            {data.orgId == null ? <Badge variant="outline">personal</Badge> : null}
            {data.isRated ? <Badge>ELO</Badge> : null}
            {data.hasResults ? <Badge variant="outline">has results</Badge> : null}
          </div>
          <div className="font-mono text-[11px] text-zinc-500 tabular-nums">
            создал {creatorLabel} · {new Date(data.createdAt).toLocaleString('ru')}
          </div>
        </div>
      </div>

      <DemoToolsCard activityId={id} />

      {/* Status panel */}
      <Card>
        <CardHeader>
          <CardTitle>Управление статусом</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
              <DialogTrigger asChild>
                <Button variant="destructive" disabled={isCancelled}>Отменить</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Отменить игру?</DialogTitle>
                </DialogHeader>
                <div className="text-sm text-zinc-400 space-y-2">
                  <div>Это нельзя отменить. Действие будет записано в аудит.</div>
                  <Label>Причина (опционально)</Label>
                  <Input
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="например, отмена по погоде"
                  />
                </div>
                <DialogFooter>
                  <Button
                    variant="destructive"
                    disabled={cancel.isPending}
                    onClick={() => cancel.mutate(cancelReason.trim() || undefined)}
                  >
                    {cancel.isPending ? '...' : 'Отменить игру'}
                  </Button>
                </DialogFooter>
                {cancel.isError && (
                  <div className="text-rose-300 text-xs mt-2">
                    {cancel.error instanceof ApiError ? cancel.error.message : 'Ошибка'}
                  </div>
                )}
              </DialogContent>
            </Dialog>

            <Dialog open={finishOpen} onOpenChange={setFinishOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" disabled={isFinished || isCancelled}>Завершить</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Завершить игру?</DialogTitle>
                </DialogHeader>
                <div className="text-sm text-zinc-400 space-y-2">
                  <div>Это нельзя отменить. Действие будет записано в аудит.</div>
                  <div>Игра уйдёт в статус <code className="font-mono text-zinc-200">finished</code>.</div>
                </div>
                <DialogFooter>
                  <Button disabled={finish.isPending} onClick={() => finish.mutate()}>
                    {finish.isPending ? '...' : 'Завершить'}
                  </Button>
                </DialogFooter>
                {finish.isError && (
                  <div className="text-rose-300 text-xs mt-2">
                    {finish.error instanceof ApiError ? finish.error.message : 'Ошибка'}
                  </div>
                )}
              </DialogContent>
            </Dialog>

            <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" disabled={data.status === 'open' || data.status === 'draft'}>
                  Переоткрыть
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Переоткрыть игру?</DialogTitle>
                </DialogHeader>
                <div className="text-sm text-zinc-400 space-y-2">
                  <div>Это нельзя отменить. Действие будет записано в аудит.</div>
                  <div>Игра вернётся в статус <code className="font-mono text-zinc-200">open</code>.</div>
                </div>
                <DialogFooter>
                  <Button disabled={reopen.isPending} onClick={() => reopen.mutate()}>
                    {reopen.isPending ? '...' : 'Переоткрыть'}
                  </Button>
                </DialogFooter>
                {reopen.isError && (
                  <div className="text-rose-300 text-xs mt-2">
                    {reopen.error instanceof ApiError ? reopen.error.message : 'Ошибка'}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Edit form */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Параметры игры</CardTitle>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-[11px] text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            {showAdvanced ? 'Скрыть advanced' : 'Показать advanced'}
          </button>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <SectionHeading>Основное</SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-1 sm:col-span-2">
                <Label>Название</Label>
                <Input
                  value={form.title ?? ''}
                  onChange={(e) => setForm({ ...form, title: e.target.value || null })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Дата (YYYY-MM-DD)</Label>
                <Input
                  className="font-mono"
                  value={form.date ?? ''}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Время (HH:MM:SS)</Label>
                <Input
                  className="font-mono"
                  value={form.time ?? ''}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Длительность (мин)</Label>
                <Input
                  className="font-mono"
                  type="number"
                  value={form.durationMinutes ?? ''}
                  onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Спорт</Label>
                <Select
                  value={form.sportType ?? ''}
                  onValueChange={(v) => setForm({ ...form, sportType: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Выбери спорт" /></SelectTrigger>
                  <SelectContent>
                    {SPORT_IDS.map((s) => (
                      <SelectItem key={s} value={s}>{getSportLabel(s, 'ru')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Max участников</Label>
                <Input
                  className="font-mono"
                  type="number"
                  value={form.maxParticipants ?? ''}
                  onChange={(e) => setForm({
                    ...form,
                    maxParticipants: e.target.value === '' ? null : Number(e.target.value),
                  })}
                />
              </div>
              <div className="space-y-1.5 flex items-end gap-2">
                <Checkbox
                  checked={!!form.hasParticipantLimit}
                  onCheckedChange={(v) => setForm({ ...form, hasParticipantLimit: !!v })}
                  id="hasParticipantLimit"
                />
                <Label htmlFor="hasParticipantLimit" className="cursor-pointer">Есть лимит</Label>
              </div>
            </div>
          </div>

          <div>
            <SectionHeading>Локация</SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-1 sm:col-span-2">
                <Label>Площадка</Label>
                <Combobox
                  options={venueOptions}
                  value={form.venueId ?? null}
                  onChange={(v) => setForm({ ...form, venueId: v })}
                  placeholder={data.venue ? data.venue.name : 'Без площадки'}
                  searchPlaceholder="Поиск площадки…"
                  emptyText={venueList ? 'Не найдено' : 'Загрузка…'}
                />
              </div>
              <div className="space-y-1.5 col-span-1 sm:col-span-2">
                <Label>Location text</Label>
                <Input
                  value={form.locationText ?? ''}
                  onChange={(e) => setForm({ ...form, locationText: e.target.value || null })}
                />
              </div>
            </div>
          </div>

          <div>
            <SectionHeading>Видимость</SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={!!form.isPublic}
                  onCheckedChange={(v) => setForm({ ...form, isPublic: !!v })}
                  id="isPublic"
                />
                <Label htmlFor="isPublic" className="cursor-pointer">Публичная</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={!!form.isRated}
                  onCheckedChange={(v) => setForm({ ...form, isRated: !!v })}
                  id="isRated"
                />
                <Label htmlFor="isRated" className="cursor-pointer">Рейтинговая</Label>
              </div>
            </div>
          </div>

          {showAdvanced && (
            <div>
              <SectionHeading>Advanced</SectionHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Format</Label>
                  <Select
                    value={form.format ?? 'pickup'}
                    onValueChange={(v) => setForm({ ...form, format: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FORMATS.map((f) => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Team mode</Label>
                  <Select
                    value={form.teamMode ?? '2_teams'}
                    onValueChange={(v) => setForm({ ...form, teamMode: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TEAM_MODES.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Scoring mode</Label>
                  <Select
                    value={form.scoringMode ?? 'team_score'}
                    onValueChange={(v) => setForm({ ...form, scoringMode: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SCORING_MODES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Signup mode</Label>
                  <Select
                    value={form.signupMode ?? 'open'}
                    onValueChange={(v) => setForm({ ...form, signupMode: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SIGNUP_MODES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!form.hasAnnouncement}
                    onCheckedChange={(v) => setForm({ ...form, hasAnnouncement: !!v })}
                    id="hasAnnouncement"
                  />
                  <Label htmlFor="hasAnnouncement" className="cursor-pointer">Анонс</Label>
                </div>
                <div className="space-y-1.5">
                  <Label>Announcement time</Label>
                  <Input
                    className="font-mono"
                    value={form.announcementTime ?? ''}
                    onChange={(e) => setForm({ ...form, announcementTime: e.target.value || null })}
                    placeholder="ISO timestamp"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 items-center pt-2 border-t border-zinc-900">
            <Button
              onClick={() => save.mutate(patchPayload)}
              disabled={save.isPending || Object.keys(patchPayload).length === 0}
            >
              {save.isPending ? 'Сохранение…' : 'Сохранить'}
            </Button>
            <span className="text-[11px] text-zinc-500">
              {Object.keys(patchPayload).length > 0
                ? <>Изменено: <span className="font-mono">{Object.keys(patchPayload).join(', ')}</span></>
                : 'Нет изменений'}
            </span>
            {save.isError && (
              <span className="text-rose-300 text-sm self-center">
                {save.error instanceof ApiError ? save.error.message : 'Ошибка сохранения'}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Roster */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>
            Состав
            <span className="ml-2 text-[11px] font-normal text-zinc-500">
              записи <span className="font-mono tabular-nums">{data.signupsCount}</span>
              {' · '}
              игроки <span className="font-mono tabular-nums">{data.participantsCount}</span>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.roster.length === 0 ? (
            <div className="text-xs text-zinc-500">Никого нет.</div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-zinc-800/80">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 bg-bg-1">
                  <tr className="border-b border-zinc-900">
                    <th className="text-left py-2 pl-4 pr-4 font-medium">Игрок</th>
                    <th className="text-left py-2 pr-4 font-medium">Email</th>
                    <th className="text-left py-2 pr-4 font-medium">Signup</th>
                    <th className="text-left py-2 pr-4 font-medium">Played</th>
                    <th className="text-left py-2 pr-4 font-medium">No-show</th>
                    <th className="text-right py-2 pr-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.roster.map((r) => (
                    <tr key={r.userId} className="border-b border-zinc-900/80 last:border-0 hover:bg-zinc-900/40">
                      <td className="py-2 pl-4 pr-4 text-zinc-200">{rosterName(r)}</td>
                      <td className="py-2 pr-4 text-zinc-500 text-xs">{r.email ?? '—'}</td>
                      <td className="py-2 pr-4">
                        {r.signupStatus ? <Badge variant="secondary">{r.signupStatus}</Badge> : <span className="text-zinc-600">—</span>}
                      </td>
                      <td className="py-2 pr-4 text-zinc-300">{r.played ? '✓' : <span className="text-zinc-700">—</span>}</td>
                      <td className="py-2 pr-4 text-zinc-300">{r.noShow ? '✓' : <span className="text-zinc-700">—</span>}</td>
                      <td className="py-2 pr-4 text-right">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={rosterRemovingId === r.userId}
                            >
                              Убрать
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Убрать игрока?</DialogTitle>
                            </DialogHeader>
                            <div className="text-sm text-zinc-400 space-y-1">
                              <div>Это нельзя отменить. Действие будет записано в аудит.</div>
                              <div>
                                Будут удалены записи в <code className="font-mono text-zinc-200">activity_signups</code> и{' '}
                                <code className="font-mono text-zinc-200">activity_participants</code> для {rosterName(r)}.
                              </div>
                            </div>
                            <DialogFooter>
                              <Button
                                variant="destructive"
                                disabled={rosterRemove.isPending}
                                onClick={() => rosterRemove.mutate(r.userId)}
                              >
                                {rosterRemove.isPending ? '...' : 'Убрать'}
                              </Button>
                            </DialogFooter>
                            {rosterRemove.isError && (
                              <div className="text-rose-300 text-xs mt-2">
                                {rosterRemove.error instanceof ApiError ? rosterRemove.error.message : 'Ошибка'}
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Telegram */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Telegram</CardTitle>
          <div className="flex items-center gap-3">
            {isFinished && telegramData && !telegramData.flags.mvpVotingClosed && (
              <Button
                variant="outline"
                size="sm"
                disabled={advanceLifecycle.isPending}
                onClick={() => advanceLifecycle.mutate()}
                title="Запостить MVP-голосование или опубликовать счёт для застрявшей игры"
              >
                {advanceLifecycle.isPending ? '...' : 'Продвинуть цикл'}
              </Button>
            )}
            {telegramData?.chatLink ? (
              <a
                href={telegramData.chatLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-200 transition-colors"
              >
                Открыть чат <ExternalLink className="h-3 w-3" />
              </a>
            ) : telegramData ? (
              <span className="text-[11px] text-zinc-600">Чат не привязан</span>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {!telegramData ? (
            <div className="text-xs text-zinc-500">Загрузка…</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Messages */}
              <div>
                <SectionHeading>Сообщения</SectionHeading>
                <div className="space-y-2">
                  {telegramData.messages.map((m) => {
                    const tone = tgToneFor(m.kind, m.messageId, m.stateNote, telegramData.flags);
                    return (
                      <div
                        key={m.kind}
                        className="flex items-center gap-3 rounded-md border border-zinc-900/80 bg-bg-1 px-3 py-2"
                      >
                        <span className="text-zinc-400"><TgIcon kind={m.kind} /></span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-zinc-200">
                            {TG_KIND_LABEL[m.kind] ?? m.kind}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant={tone}>{m.stateNote}</Badge>
                            {m.messageId ? (
                              <span
                                className="font-mono tabular-nums text-[10px] text-zinc-500 truncate max-w-[12ch]"
                                title={m.messageId}
                              >
                                msg #{m.messageId}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        {(['announcement', 'signup', 'end', 'mvp'] as const).includes(
                          m.kind as 'announcement' | 'signup' | 'end' | 'mvp',
                        ) && m.messageId ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                if (resendPhoto.isPending) return;
                                if (!window.confirm(
                                  `Прислать картинку к «${TG_KIND_LABEL[m.kind] ?? m.kind}»?\n\n` +
                                  `Текст сообщения (msg #${m.messageId}) не тронется — только попробуем прикрепить фото сверху.`,
                                )) return;
                                const apiKind =
                                  m.kind === 'end'
                                    ? 'finish'
                                    : (m.kind as 'announcement' | 'signup' | 'mvp');
                                resendPhoto.mutate(apiKind);
                              }}
                              disabled={resendPhoto.isPending}
                              className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-200 disabled:opacity-40 transition-colors"
                              title="Прислать картинку"
                            >
                              <ImagePlus className={`h-3 w-3 ${resendPhoto.isPending ? 'animate-pulse' : ''}`} />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (repost.isPending) return;
                                if (!window.confirm(
                                  `Удалить старое TG-сообщение и отправить заново?\n\n` +
                                  `Старая «${TG_KIND_LABEL[m.kind] ?? m.kind}» (msg #${m.messageId}) будет удалена ` +
                                  `(если боту разрешено), записи и голоса сохранятся.`,
                                )) return;
                                const apiKind =
                                  m.kind === 'end'
                                    ? 'finish'
                                    : (m.kind as 'announcement' | 'signup' | 'mvp');
                                repost.mutate(apiKind);
                              }}
                              disabled={repost.isPending}
                              className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-200 disabled:opacity-40 transition-colors"
                              title="Переотправить"
                            >
                              <RefreshCw className={`h-3 w-3 ${repost.isPending ? 'animate-spin' : ''}`} />
                            </button>
                          </>
                        ) : null}
                        {m.link ? (
                          <a
                            href={m.link}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-100 transition-colors"
                          >
                            Открыть <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-[11px] text-zinc-700">—</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Photos */}
              <div>
                <SectionHeading>Превью медиа</SectionHeading>
                <div className="space-y-4">
                  <div>
                    <div className="text-[11px] text-zinc-500 mb-1.5">OG Share</div>
                    <img
                      src={telegramData.photos.ogShare}
                      alt="OG Share preview"
                      loading="lazy"
                      className="max-w-md w-full rounded-md border border-zinc-900/80"
                    />
                  </div>
                  <div>
                    <div className="text-[11px] text-zinc-500 mb-1.5">Leaderboard</div>
                    {telegramData.photos.leaderboard ? (
                      <img
                        src={telegramData.photos.leaderboard}
                        alt="Leaderboard preview"
                        loading="lazy"
                        className="max-w-md w-full rounded-md border border-zinc-900/80"
                      />
                    ) : (
                      <div className="text-[11px] text-zinc-600 italic">
                        Доступно когда игра завершена
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* MVP */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2">
            MVP голосование
            {mvpData ? (
              <Badge variant={mvpData.mvpVotingClosed ? 'warning' : 'success'}>
                {mvpData.mvpVotingClosed ? 'Закрыто' : 'Открыто'}
              </Badge>
            ) : null}
          </CardTitle>
          {mvpData ? (
            <div className="text-[11px] text-zinc-500 font-mono tabular-nums">
              Участников: {mvpData.totalParticipants} · Проголосовало: {mvpData.totalVoters} /{' '}
              {mvpData.totalParticipants}
            </div>
          ) : null}
        </CardHeader>
        <CardContent>
          {!mvpData ? (
            <div className="text-xs text-zinc-500">Загрузка…</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                {mvpData.mvps.length > 0 ? (
                  <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2.5">
                    <div className="text-[11px] uppercase tracking-wide text-amber-500/80 mb-0.5">МВП</div>
                    <div className="text-sm font-medium text-amber-200 flex flex-wrap gap-x-2 gap-y-0.5">
                      {mvpData.mvps.map((m) => (
                        <span key={m.userId}>
                          ★ {fullName(m)}
                          {m.team ? <span className="text-amber-500/70"> ({m.team})</span> : null}
                          {m.votes != null ? <span className="text-amber-500/70 font-mono"> · {m.votes}</span> : null}
                          {m.tiebreaker ? <span className="text-amber-500/70"> · по андердогу</span> : null}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : mvpData.mvpVotingClosed ? (
                  <div className="mb-3 text-xs text-zinc-500">
                    Голосование закрыто, MVP ещё не финализирован (опубликуй счёт/статистику).
                  </div>
                ) : null}
                <SectionHeading>Топ по голосам</SectionHeading>
                {mvpData.voteTally.length === 0 ? (
                  <div className="text-xs text-zinc-500">Пока никто не проголосовал.</div>
                ) : (
                  <div className="space-y-1.5">
                    {mvpData.voteTally.map((row, idx) => {
                      const expanded = !!expandedVoters[row.userId];
                      const collapsedLimit = 5;
                      const showAll = expanded || row.voters.length <= collapsedLimit;
                      const visibleVoters = showAll
                        ? row.voters
                        : row.voters.slice(0, collapsedLimit);
                      return (
                        <div
                          key={row.userId}
                          className="rounded-md border border-zinc-900/80 bg-bg-1 px-3 py-2"
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-mono tabular-nums text-[11px] text-zinc-500 w-5 text-right">
                              {idx + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-zinc-200 truncate">
                                {mvpData.mvps.some((m) => m.userId === row.userId) ? (
                                  <span className="text-amber-400" title="MVP">★ </span>
                                ) : null}
                                {fullName(row)}
                              </div>
                              {row.email ? (
                                <div className="text-[11px] text-zinc-500 truncate">
                                  {row.email}
                                </div>
                              ) : null}
                            </div>
                            <Badge variant="secondary" className="font-mono tabular-nums">
                              {row.voteCount}
                            </Badge>
                          </div>
                          {row.voters.length > 0 ? (
                            <div className="mt-1.5 pl-8 text-[11px] text-zinc-500">
                              от{' '}
                              {visibleVoters
                                .map((v) => fullName(v))
                                .join(', ')}
                              {!showAll ? (
                                <>
                                  {' '}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setExpandedVoters((s) => ({ ...s, [row.userId]: true }))
                                    }
                                    className="text-zinc-400 hover:text-zinc-200 transition-colors"
                                  >
                                    +{row.voters.length - collapsedLimit} ещё
                                  </button>
                                </>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <SectionHeading>Ещё не голосовали</SectionHeading>
                {mvpData.pendingVoters.length === 0 ? (
                  <div className="text-xs text-zinc-500">
                    {mvpData.totalParticipants === 0
                      ? 'Нет участников.'
                      : 'Все участники проголосовали.'}
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {mvpData.pendingVoters.map((p) => (
                      <li
                        key={p.userId}
                        className="text-sm text-zinc-300 flex items-baseline gap-2"
                      >
                        <span>{fullName(p)}</span>
                        {p.email ? (
                          <span className="text-[11px] text-zinc-500">{p.email}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
